
import { useState, useRef } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UnitSystem } from '@/utils/unitConversion/types';
import { ShoppingListResponse } from '@/types/shoppingList';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useUser } from '@/contexts/UserContext';

interface UseShoppingListGenerationOptions {
  preferredUnits: UnitSystem;
  onShoppingListGenerated: (list: ShoppingListResponse) => void;
}

export const useShoppingListGeneration = (options: UseShoppingListGenerationOptions) => {
  const { preferredUnits, onShoppingListGenerated } = options;
  const [isLoadingShoppingList, setIsLoadingShoppingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingStage, setProcessingStage] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { familyId } = useCurrentUserFamily();
  const { user } = useUser();

  const logShoppingListActivity = async (
    selectedDates: Date[],
    adults: number,
    kids: number,
    recipeCount: number
  ) => {
    if (!familyId || !user?.id) return;

    try {
      const startDate = format(selectedDates[0], 'MMM dd');
      const endDate = format(selectedDates[selectedDates.length - 1], 'MMM dd');
      const dateRange = selectedDates.length === 1 ? startDate : `${startDate} - ${endDate}`;

      await supabase.from('family_activity_logs').insert({
        family_id: familyId,
        user_id: user.id,
        activity_type: 'shopping_list_generated',
        entity_type: 'shopping_list',
        entity_id: crypto.randomUUID(),
        entity_name: `Shopping List for ${dateRange}`,
        metadata: {
          date_range: dateRange,
          recipe_count: recipeCount,
          family_size: { adults, kids }
        }
      });
    } catch (error) {
      console.error('Failed to log shopping list activity:', error);
    }
  };

  const generateShoppingList = async (
    selectedDates: Date[],
    adults: number,
    kids: number
  ) => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    try {
      setIsLoadingShoppingList(true);
      setError(null);
      setProgress(10);
      setProcessingStage('Analyzing meal plans...');
      
      // Start smooth progress simulation
      const startProgressSimulation = () => {
        const interval = setInterval(() => {
          setProgress(prev => {
            if (prev < 30) return prev + 5;
            if (prev < 60) return prev + 3;
            if (prev < 90) return prev + 1;
            return prev; // Cap at 90% until completion
          });
        }, 800);
        progressIntervalRef.current = interval;
        return interval;
      };
      
      startProgressSimulation();
      
      // Find earliest and latest selected dates
      const sortedDates = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];
      
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      // Include selected dates for more precise control
      const selectedDateStrings = selectedDates.map(date => format(date, 'yyyy-MM-dd'));
      
      console.log('Generating shopping list with params:', {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        selectedDates: selectedDateStrings,
        familySize: { adults, kids },
        unitSystem: preferredUnits
      });
      
      // Update progress stages based on current progress
      setTimeout(() => setProcessingStage('Extracting recipe details...'), 8000);
      setTimeout(() => setProcessingStage('Calculating ingredients...'), 16000);
      setTimeout(() => setProcessingStage('Optimizing shopping list...'), 24000);
      setTimeout(() => setProcessingStage('Almost done...'), 30000);
      
      const { data, error } = await supabase.functions.invoke('generate-shopping-list', {
        body: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          selectedDates: selectedDateStrings,
          familySize: { adults, kids },
          unitSystem: preferredUnits
        }
      });
      
      // Clear progress interval and complete
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      
      if (error) {
        console.error('Error from edge function:', error);
        throw new Error(error.message || 'Failed to generate shopping list');
      }
      
      if (!data) {
        throw new Error('No data received from shopping list generation');
      }
      
      console.log('Shopping list generated successfully:', data);
      
      // Add generation parameters to the shopping list
      const shoppingListWithParams = {
        ...data,
        generationParams: {
          adults,
          kids,
          unitSystem: preferredUnits,
          selectedDates: selectedDateStrings,
        },
      };
      
      // Log the activity
      const recipeCount = data.recipes?.length || 0;
      await logShoppingListActivity(selectedDates, adults, kids, recipeCount);
      
      // Complete progress
      setProgress(100);
      setProcessingStage('Complete!');
      
      onShoppingListGenerated(shoppingListWithParams);
      toast.success('Shopping list generated successfully');
      
    } catch (err: any) {
      console.error('Error generating shopping list:', err);
      const errorMessage = err.message || 'Failed to generate shopping list';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      // Clean up progress interval using ref (avoids stale closure)
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setIsLoadingShoppingList(false);
      setProgress(0);
      setProcessingStage('');
    }
  };

  return {
    isLoadingShoppingList,
    error,
    processingStage,
    progress,
    generateShoppingList,
    setError
  };
};